import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './PublicationModal.css';

export default function PublicationModal({ pub, onClose }) {
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  if (!pub) return null;

  return createPortal(
    <div className="pub-modal" role="dialog" aria-modal="true" aria-labelledby="modal-p-title">
      <div className="pub-modal__backdrop" onClick={onClose} />
      
      <div className="pub-modal__container">
        <button className="pub-modal__close" onClick={onClose} aria-label="Close modal">✕</button>
        
        <div className="pub-modal__body">
          {pub.coverUrl && (
            <div className="pub-modal__image-side">
              <img src={pub.coverUrl} alt={`Cover for ${pub.title}`} className="pub-modal__img" />
            </div>
          )}
          
          <div className="pub-modal__info-side">
            <header className="pub-modal__header">
              <div className="pub-modal__meta">
                <span className="badge badge-navy">{pub.category}</span>
                <span className="pub-modal__year">{pub.year || pub.date}</span>
              </div>
              <h2 id="modal-p-title" className="pub-modal__title">{pub.title}</h2>
              <p className="pub-modal__source">
                {pub.journal || pub.publisher || pub.outlet || pub.bookTitle}
                {pub.volume && `, Vol. ${pub.volume}`}
                {pub.issue  && `, No. ${pub.issue}`}
                {pub.pages  && `, pp. ${pub.pages}`}
              </p>
            </header>

            <div className="pub-modal__content">
              {(pub.abstract || pub.description) && (
                <div className="pub-modal__section">
                  <h4>Abstract / Description</h4>
                  <p className="pub-modal__description">{pub.abstract || pub.description}</p>
                </div>
              )}

              {pub.tags?.length > 0 && (
                <div className="pub-modal__section">
                  <h4>Keywords</h4>
                  <div className="pub-modal__tags">
                    {pub.tags.map(t => <span key={t} className="badge badge-outline">{t}</span>)}
                  </div>
                </div>
              )}
            </div>

            <footer className="pub-modal__footer">
              <div className="pub-modal__actions">
                {pub.doi && (
                  <a 
                    href={pub.doi.startsWith('http') ? pub.doi : `https://doi.org/${pub.doi.trim().replace(/^doi:/i, '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-outline btn-sm"
                  >
                    View DOI →
                  </a>
                )}
                {pub.url && (
                  <a href={pub.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
                    Google Scholar →
                  </a>
                )}
                {pub.fileUrl && (
                  <a href={pub.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm" download>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '0.4rem'}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                    Download PDF
                  </a>
                )}
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
